import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePageAccess, ASSIGNABLE_PAGES, type PageKey } from '@/hooks/use-page-access';
import { AuthLoadingScreen } from '@/components/AuthLoadingScreen';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requirePage?: PageKey;
}

export function ProtectedRoute({ children, requirePage }: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const { hasAccess, pages, isLoading: pagesLoading } = usePageAccess();

  // Always wait for role/page-access resolution before deciding access.
  // Otherwise super_admin (whose role depends on the local token) is briefly
  // treated as no-role and redirected, causing a Navigate render loop.
  if (loading || (requirePage && pagesLoading)) {
    return <AuthLoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (requirePage && !hasAccess(requirePage)) {
    // Redirect to the user's first allowed page. If none, show a no-access
    // screen — NEVER bounce back to /auth while the user is authenticated,
    // that creates a redirect loop with /auth's "user => Navigate to /".
    const fallback = ASSIGNABLE_PAGES.find((p) => pages.has(p.key));
    if (fallback) {
      return <Navigate to={`/${fallback.key}`} replace />;
    }
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-md text-center space-y-3">
          <h1 className="text-2xl font-semibold">No access</h1>
          <p className="text-muted-foreground">
            You don't have access to this page. Please contact an administrator.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
